import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { binomialPmf, distributionMoments } from '../prob/distributions';
import { binomialNormalCorrected, continuityCorrectedRange } from '../prob/continuity-correction';
import { normalPdf } from '../prob/normal';
import { createPmfChart, type PmfChartHandle } from '../render/pmf-chart';
import { createSvgEl } from '@learnforge/demo-render';
import type { ProbContext, ProbKey } from '../types';
import { createSliderRow, createToggleRow } from '@learnforge/demo-kit';
import { paramFormat } from '../ui/param-format';
import { createActionBar } from '../action-bar';

// ── Pure helper (exported + tested) ──────────────────────────────────────────

export function simulateGaltonDrop(rows: number, p: number, prng: { next(): number }): number {
  let col = 0;
  for (let r = 0; r < rows; r++) {
    if (prng.next() >= p) col++;
  }
  return col;
}

// ── State (model only — no DOM refs) ─────────────────────────────────────────

interface State {
  rows: number;
  p: number;
  showBinomial: boolean;
  showNormal: boolean;
  showContinuityBins: boolean;
  speed: number;
  bins: number[];
  totalDrops: number;
  auto: boolean;
}

// ── Spec factory ─────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<State, ProbKey, ProbContext> {
  return {
    labelKey: 'experiment.galton-board.label',
    demoId: 'probability',
    advancedKey: 'galton-board',

    initialState(_ctx): State {
      const rows = 12;
      return {
        rows,
        p: 0.5,
        showBinomial: true,
        showNormal: false,
        showContinuityBins: false,
        speed: 1,
        bins: new Array<number>(rows + 1).fill(0),
        totalDrops: 0,
        auto: false,
      };
    },

    createRender(ctx, host, controls) {
      // ── Stage SVG (built once) ────────────────────────────────────────────
      const stageSvg = createSvgEl('svg', { viewBox: '0 0 800 600', class: 'pb-gb-svg' });
      host.appendChild(stageSvg);
      const stageGroup = createSvgEl('g');
      stageSvg.appendChild(stageGroup);

      // Transient closure vars — not in State
      let chart: PmfChartHandle | null = null;
      let autoTimer: number | null = null;
      let lastAuto = false;
      // Live speed ref — kept in sync at the top of every update() so the
      // auto-drop interval always uses the current speed, not the captured value
      // from the tick that started the timer.
      let speedRef = 1;

      // Register cleanup so dispose always cancels a running timer
      controls.registerCleanup(() => {
        if (autoTimer !== null) {
          window.clearInterval(autoTimer);
          autoTimer = null;
        }
      });

      // ── Drop helper — reads current state via closure, pushes back immutably ──
      function drop(count: number): void {
        controls.setState((s) => {
          const bins = s.bins.slice();
          for (let i = 0; i < count; i++) {
            const bin = simulateGaltonDrop(s.rows, s.p, ctx.prng);
            bins[bin] = (bins[bin] ?? 0) + 1;
          }
          return { ...s, bins, totalDrops: s.totalDrops + count };
        });
      }

      // ── Panel (built once into ctx.panel) ────────────────────────────────
      function addSlider(
        labelKey: ProbKey,
        min: number,
        max: number,
        step: number,
        value: number,
        onChange: (v: number) => void,
        format?: (v: number) => string,
      ): void {
        const row = createSliderRow({
          id: `gb-${labelKey}`,
          label: ctx.i18n.t(labelKey),
          i18n: ctx.i18n,
          min,
          max,
          step,
          value,
          format: format ?? paramFormat,
          onChange,
        });
        ctx.panel.appendChild(row.element);
      }

      addSlider('param.rows.label', 3, 20, 1, 12, (v) => {
        controls.setState((s) => {
          const newRows = v;
          const bins = new Array<number>(newRows + 1).fill(0);
          // Carry over counts that still fit
          for (let i = 0; i < s.bins.length && i <= newRows; i++) {
            bins[i] = s.bins[i] ?? 0;
          }
          return { ...s, rows: newRows, bins };
        });
      });

      addSlider(
        'param.p.label',
        0.01,
        0.99,
        0.01,
        0.5,
        (v) => controls.setState((s) => ({ ...s, p: v })),
        (v) => v.toFixed(2),
      );

      addSlider('param.speed.label', 1, 10, 1, 1, (v) =>
        controls.setState((s) => ({ ...s, speed: v })),
      );

      // Overlay checkboxes
      const toggles: Array<
        readonly [ProbKey, keyof Pick<State, 'showBinomial' | 'showNormal' | 'showContinuityBins'>]
      > = [
        ['param.show-binomial-theoretical.label', 'showBinomial'],
        ['param.show-normal-approx.label', 'showNormal'],
        ['param.show-continuity-bins.label', 'showContinuityBins'],
      ] as const;

      for (const [key, field] of toggles) {
        const toggle = createToggleRow({
          labelKey: key,
          i18n: ctx.i18n,
          checked: field === 'showBinomial',
          onChange: (checked) => {
            controls.setState((s) => ({ ...s, [field]: checked }));
          },
        });
        toggle.input.id = `gb-${key}`;
        ctx.panel.appendChild(toggle.element);
      }

      // ── Action bar (built once via createActionBar) ───────────────────────
      const autoBtn = document.createElement('button');
      autoBtn.className = 'pb-actionbar-btn';
      autoBtn.textContent = ctx.i18n.t('btn.auto-drop');
      autoBtn.addEventListener('click', () => {
        controls.setState((s) => ({ ...s, auto: !s.auto }));
      });

      const actionBar = createActionBar(ctx.actionBar, [
        { id: 'drop-one', labelKey: ctx.i18n.t('btn.drop-one'), onClick: () => drop(1) },
        { id: 'drop-100', labelKey: ctx.i18n.t('btn.drop-100'), onClick: () => drop(100) },
        { id: 'drop-1000', labelKey: ctx.i18n.t('btn.drop-1000'), onClick: () => drop(1000) },
        {
          id: 'reset',
          labelKey: ctx.i18n.t('btn.reset'),
          onClick: () =>
            controls.setState((s) => ({
              ...s,
              bins: new Array<number>(s.rows + 1).fill(0),
              totalDrops: 0,
              auto: false,
            })),
        },
      ]);
      // Append the auto-drop button manually (createActionBar renders the others)
      ctx.actionBar.appendChild(autoBtn);

      // ── update — called after every state change ──────────────────────────
      const update = (state: State): void => {
        // Keep speedRef in sync so the running auto-drop interval always uses
        // the current speed value, not the stale captured value from when the
        // timer was started.
        speedRef = state.speed;

        // 1. Reconcile the pmf-chart
        chart?.destroy();
        stageGroup.replaceChildren();
        const empiricalBars = state.bins.map((n, k) => ({
          x: k,
          p: state.totalDrops > 0 ? n / state.totalDrops : 0,
        }));
        const theoreticalBars = state.showBinomial
          ? Array.from({ length: state.rows + 1 }, (_, k) => ({
              x: k,
              p: binomialPmf(state.rows, state.p, k),
            }))
          : [];
        const moments = distributionMoments({ kind: 'binomial', n: state.rows, p: state.p });
        const options = {
          xDomain: [-0.5, state.rows + 0.5] as const,
          bars: theoreticalBars.length > 0 ? theoreticalBars : empiricalBars,
          empiricalBars:
            theoreticalBars.length > 0 ? empiricalBars : (undefined as unknown as never),
          normalOverlay: state.showNormal
            ? { mu: moments.mean, sigma: Math.sqrt(moments.variance), opacity: 0.6 }
            : (undefined as unknown as never),
          xLabelKey: 'axis.count-bin',
          yLabelKey: 'axis.probability',
          i18n: ctx.i18n,
        } as unknown as Parameters<typeof createPmfChart>[1];
        chart = createPmfChart(stageGroup, options);

        if (state.showContinuityBins) {
          for (let k = 0; k <= state.rows; k++) {
            const pCont = binomialNormalCorrected(state.rows, state.p, k, k);
            void pCont;
            void normalPdf;
            void continuityCorrectedRange;
          }
        }

        // 2. Drive the auto-timer from state.auto transitions
        if (state.auto && !lastAuto) {
          // false → true: start timer
          autoTimer = window.setInterval(() => drop(10 * speedRef), 33);
        } else if (!state.auto && lastAuto) {
          // true → false: stop timer
          if (autoTimer !== null) {
            window.clearInterval(autoTimer);
            autoTimer = null;
          }
        }
        lastAuto = state.auto;
      };

      return {
        update,
        dispose() {
          // Narrow dispose: timers and chart only.
          // The harness's registered cleanup also clears the timer (belt-and-suspenders).
          if (autoTimer !== null) {
            window.clearInterval(autoTimer);
            autoTimer = null;
          }
          chart?.destroy();
          chart = null;
          actionBar.destroy();
        },
      };
    },

    applyShotState(state, _shotId): State {
      return { ...state, auto: false };
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Registry alias — keeps index.ts and the existing tests green without any edits
export const mountGaltonBoard = experiment.mount.bind(experiment);
